import { css } from '@emotion/react';
import color from 'components/common/color';

const MUTED = '#5a6478';
const BORDER = '#dfe3ea';
const FILL = '#f3f5f8';
const PLACEHOLDER = '#8a93a6';
const CONTROL_HEIGHT = '38px';

export const sidebarStyles = css`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  flex: 0 0 288px;
  width: 288px;
  background-color: ${color.WHITE};
  border-right: 1px solid ${BORDER};
`;

export const railStyles = css`
  position: relative;
  z-index: 2;
  flex: 0 0 48px;
  display: flex;
  justify-content: center;
  padding-top: 12px;
  background-color: ${color.WHITE};
  border-right: 1px solid ${BORDER};
`;

export const railButtonStyles = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  background: ${color.WHITE};
  color: ${color.BLUE};
  cursor: pointer;

  i {
    font-size: 14px;
    color: inherit;
  }

  span {
    position: absolute;
    top: -6px;
    right: -6px;
  }

  &:hover {
    background-color: #f2f4f8;
  }
`;

export const headerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 12px 12px 20px;
`;

export const headerTitleStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${color.BLUE};

  i {
    font-size: 12px;
    color: inherit;
  }
`;

export const badgeStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
  color: ${color.WHITE};
  background-color: ${color.ORANGE};
`;

export const collapseButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: ${color.TRANSPARENT};
  color: ${MUTED};
  cursor: pointer;

  i {
    font-size: 13px;
    color: inherit;
  }

  &:hover {
    background-color: #f2f4f8;
    color: ${color.BLUE};
  }
`;

export const searchFormStyles = css`
  position: relative;
  padding: 0 20px 14px;
  border-bottom: 1px solid ${BORDER};
`;

export const searchIconStyles = css`
  position: absolute;
  left: 32px;
  top: 12px;
  font-size: 13px;
  color: ${MUTED};
  pointer-events: none;
`;

export const searchInputStyles = css`
  box-sizing: border-box;
  width: 100%;
  height: 38px;
  padding: 0 12px 0 34px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  font-size: 14px;
  background-color: #f6f7f9;

  &:hover, &:focus {
    opacity: 1;
    outline: none;
    border-color: ${color.LIGHT_BLUE};
    background-color: ${color.WHITE};
  }
`;

export const hiddenLabelStyles = css`
  display: none;
`;

export const sidebarContainerStyles = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  & > form {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  /* Text-like fields (dates): white, bordered, leading icon. */
  & .react-datepicker-wrapper .react-datepicker__input-container {
    position: relative;

    &::before {
      content: '\\f133';
      font-family: 'Font Awesome 5 Free';
      font-weight: 400;
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 13px;
      color: ${MUTED};
      pointer-events: none;
    }

    /* Doubled class so this wins over each page's datepicker container styles. */
    .date-picker.date-picker {
      box-sizing: border-box;
      width: 100%;
      height: ${CONTROL_HEIGHT};
      padding: 0 32px 0 34px;
      border: 1px solid ${BORDER};
      border-radius: 8px;
      background-color: ${color.WHITE};
      font-size: 13px;

      &::placeholder {
        font-size: 13px;
        color: ${PLACEHOLDER};
      }

      &:hover, &:focus {
        border-color: ${color.LIGHT_BLUE};
      }
    }

    .react-datepicker__close-icon.react-datepicker__close-icon {
      padding: 0;
      right: 0;
      width: 32px;

      &::after {
        position: absolute;
        top: 50%;
        right: 10px;
        bottom: auto;
        transform: translateY(-50%);
        width: 16px;
        height: 16px;
        padding: 0;
        font-size: 12px;
        line-height: 16px;
        background-color: ${PLACEHOLDER};
      }

      &:hover::after {
        background-color: ${MUTED};
      }
    }
  }

  /* Dropdowns: filled grey control with a chevron, so they read differently from text fields. */
  & .select2__control.select2__control,
  & .select__control.select__control {
    min-height: ${CONTROL_HEIGHT};
    height: ${CONTROL_HEIGHT};
    padding: 0;
    border: 1px solid ${BORDER};
    border-radius: 8px;
    background-color: ${FILL};
    box-shadow: none;

    &:hover {
      border-color: ${color.LIGHT_BLUE};
    }
  }

  & .select2__control--is-focused.select2__control--is-focused,
  & .select__control--is-focused.select__control--is-focused {
    border-color: ${color.LIGHT_BLUE};
    background-color: ${color.WHITE};
  }

  & .select2__value-container.select2__value-container {
    padding: 0 12px;
    font-size: 13px;
  }

  & .select2__placeholder.select2__placeholder {
    font-size: 13px;
    color: ${PLACEHOLDER};
  }

  & .select2__indicator-separator,
  & .select__indicator-separator {
    display: none;
  }

  & .select2__indicator.select2__indicator svg,
  & .select__indicator.select__indicator svg {
    color: ${MUTED};
    width: 16px;
    height: 16px;
  }

  & .select__indicator.select__indicator {
    padding: 0 10px;
  }

  & .creatable-placeholder.creatable-placeholder {
    top: 50%;
    left: 12px;
    transform: translateY(-50%);
    z-index: 1;
    font-size: 13px;
    font-weight: 400;
    color: ${PLACEHOLDER};
    pointer-events: none;
  }

  & select {
    box-sizing: border-box;
    height: ${CONTROL_HEIGHT};
    padding: 0 32px 0 12px;
    border: 1px solid ${BORDER};
    border-radius: 8px;
    background-color: ${FILL};
    background-position: right 8px center;
    font-size: 13px;
    line-height: normal;
    color: ${color.BLACK};

    &:hover, &:focus {
      outline: none;
      border-color: ${color.LIGHT_BLUE};
    }
  }
`;

// Wrapper around every page's fields.
export const filterConditionsContainerStyles = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 4px 0 8px;
`;

export const sectionStyles = css`
  border-bottom: 1px solid ${BORDER};

  &:last-of-type {
    border-bottom: none;
  }
`;

export const sectionTitleStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 20px;
  border: none;
  background: ${color.TRANSPARENT};
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: ${MUTED};
  cursor: pointer;

  i {
    font-size: 10px;
    color: inherit;
  }

  &:hover {
    color: ${color.BLUE};
  }
`;

export const sectionBodyStyles = css`
  padding: 0 20px 8px;
`;

export const fieldStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
  width: 100%;

  label:empty {
    display: none;
  }
`;

export const fieldLabelStyles = css`
  font-size: 12px;
  font-weight: 500;
  color: ${MUTED};
  text-transform: capitalize;
`;

export const actionsStyles = css`
  position: sticky;
  bottom: 0;
  display: flex;
  gap: 8px;
  padding: 12px 20px;
  background-color: ${color.WHITE};
  border-top: 1px solid ${BORDER};
`;

export const applyButtonStyles = css`
  flex: 1;
  height: 38px;
  padding: 0;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
`;

export const resetButtonStyles = css`
  flex: 1;
  height: 38px;
  padding: 0;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  color: ${MUTED};
  background-color: ${color.WHITE};
  border: 1px solid ${BORDER};

  &:hover {
    opacity: 1;
    color: ${color.BLUE};
    border-color: ${color.BLUE};
  }
`;

// For short panels that list fields without sections.
export const unsectionedFieldsStyles = css`
  padding: 12px 20px 8px;
`;
